import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { Login } from "./pages/Login";
import { Conversation } from "./pages/Conversation";
import { SidebarProvider, SidebarTrigger } from "./components/ui/sidebar";
import { AppSidebar } from "./components/app-sidebar";
import { useAuth } from "./contexts/AuthContext";
import { AuthRoute } from "./components/AuthRoute";
import { UserProvider } from "./contexts/UserContext";
import { DialogProvider } from "./contexts/DialogContext";
import { NewConversationDialog } from "./components/NewConversationDialog";
import { Toaster } from "@/components/ui/toaster";
import { EmailVerification } from "./pages/EmailVerification";
import { ResetPassword } from "./pages/ResetPassword";
import { Account } from "./pages/Account";
import { UpsellDialogProvider } from "./contexts/UpsellDialogContext";
import { UpsellDialog } from "./components/UpsellDialog";
import { Help } from "./pages/Help";
import { Pricing } from "./pages/Pricing";
import ThankYou from "./pages/ThankYou";
import { PaymentStep, PasswordStep } from "./pages/NewRegistration";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { PaymentUpdateDialogProvider } from "./contexts/PaymentUpdateDialogContext";
import { PaymentUpdateDialog } from "./components/payment-update-dialog";
import { NewRegister } from "./pages/NewRegistration/NewRegister";
import { Confirmation } from "./pages/NewRegistration/Confirmation";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

function MainLayout() {
  const { currentUser, access, claims } = useAuth();

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  // Claims not resolved yet: don't route on a default access state.
  if (!claims) {
    return null;
  }

  // Never paid: go straight to the payment page. Expired or canceled users
  // get the app shell with the upsell / payment-update dialogs instead.
  if (!access.hasAccess && !access.everEntitled) {
    return <Navigate to="/register/payment" />;
  }

  return (
    <>
      <div className="h-full flex">
        <SidebarProvider>
          <AppSidebar />
          <div className="flex-1 h-full overflow-hidden">
            <div className="px-2 py-2 sm:p-4 flex items-center md:hidden absolute top-0 left-0 z-50">
              <SidebarTrigger />
            </div>
            <Routes>
              <Route path="/c/:conversationId" element={<Conversation />} />
              <Route path="/new" element={<Navigate to="/" replace />} />
              <Route path="/" element={<Conversation />} />
              <Route path="/account" element={<Account />} />
              <Route path="/support" element={<Help />} />
            </Routes>
          </div>
        </SidebarProvider>
      </div>
      <Toaster />
      <NewConversationDialog />
      <UpsellDialog />
      <PaymentUpdateDialog />
    </>
  );
}

function App() {
  return (
    <div className="h-[100dvh] w-full flex flex-col overflow-hidden">
      <Router>
        <AuthProvider>
          <UserProvider>
            <UpsellDialogProvider>
              <PaymentUpdateDialogProvider>
                <DialogProvider>
                  <Elements stripe={stripePromise}>
                    <Routes>
                      <Route path="/pricing" element={<Pricing />} />
                      <Route path="/thank-you" element={<ThankYou />} />
                      <Route
                        path="/login"
                        element={<AuthRoute element={<Login />} />}
                      />
                      <Route
                        path="/register"
                        element={<AuthRoute element={<NewRegister />} />}
                      />
                      <Route
                        path="/register/confirmation"
                        element={<AuthRoute element={<Confirmation />} />}
                      />
                      <Route
                        path="/register/payment"
                        element={<AuthRoute element={<PaymentStep />} />}
                      />
                      <Route
                        path="/register/password"
                        element={<AuthRoute element={<PasswordStep />} />}
                      />
                      <Route
                        path="/reset-password"
                        element={<AuthRoute element={<ResetPassword />} />}
                      />
                      <Route
                        path="/verify-email"
                        element={<EmailVerification />}
                      />
                      <Route path="/*" element={<MainLayout />} />
                    </Routes>
                  </Elements>
                </DialogProvider>
              </PaymentUpdateDialogProvider>
            </UpsellDialogProvider>
          </UserProvider>
        </AuthProvider>
      </Router>
    </div>
  );
}

export default App;
